<template>
  <component
    :is="noheader ? 'div' : 'section'"
    class="jps-curation-list"
    :class="!$slots.default && typeClass"
  >
    <h2
      v-if="!isEmpty(def.title) && !noheader"
      class="jps-curation-list-title h2 is-size-5-desktop is-size-6-touch has-text-weight-bold generated-5"
    >
      {{ ls(def.title) }}
    </h2>

    <slot
      v-if="$slots.default"
      :items="items"
      :def="def"
      @show-modal="showModal"
    />

    <!-- ViewCard 固有の props (props.height) を定義するため別に記述する -->
    <ViewCard
      v-else-if="def.viewType === 'card'"
      :items="items"
      :def="def"
      height="middle"
      @show-modal="showModal"
      :view-param="viewParam"
    />
    <Component
      v-else
      :is="component(def.viewType)"
      :items="items"
      :def="def"
      @show-modal="showModal"
      :view-param="viewParam"
    />
    <CurationModal ref="curationModal" />
  </component>
</template>

<script lang="ts" setup>
import type { CurationModal } from '#components'
import type { ListElement } from '~/composables/useListElement'

const componentMap = {
  // 'view-card': defineAsyncComponent(() => import('./ViewCard.vue')),
  'view-bullet': defineAsyncComponent(() => import('./ViewBullet.vue')),
  'view-description': defineAsyncComponent(() => import('./ViewDescription.vue')),
  'view-image': defineAsyncComponent(() => import('./ViewImage.vue')),
  'view-imagewide': defineAsyncComponent(() => import('./ViewImagewide.vue')),
  'view-map': defineAsyncComponent(() => import('./ViewMap.vue')),
  'view-reference': defineAsyncComponent(() => import('./ViewReference.vue')),
  'view-table': defineAsyncComponent(() => import('./ViewTable.vue')),
  'view-tile': defineAsyncComponent(() => import('./ViewTile.vue')),
  'view-blind': defineAsyncComponent(() => import('./ViewBlindWindow.vue')),
}

const component = (key: string) => {
  return componentMap['view-' + key] ||
    function () {
      return h('div', 'list-' + key)
    }
}

const { toListElement, dataToListElement, toModalInfo } = useListElement()

const SEARCH_SIZE = 7

const { ls } = useI18n()

interface Props {
  noheader?: boolean
  def: PartList
  viewParam?: CurationListViewParam
}

type SearchItem = {
  status: 'waiting' | 'searching' | 'searched' | 'end'
  def: PartListSearchResult
  nextQuery: SearchQuery
  items: ListElement[]
}

const props = withDefaults(defineProps<Props>(), {
  noheader: false,
})

const typeClass = computed(() => {
  return 'jps-curation-list-vt-' + props.def.viewType
})

const extendedParts: Ref<CurationPartDefinition[]> = ref([])
const searchItem: Ref<{ [ie: string]: SearchItem }> = ref({})

watch(
  () => props.def.parts,
  () => {
    if (props.def.parts) {
      extendedParts.value = [...props.def.parts]
      searchItem.value = {}
    }
  },
  { immediate: true },
)

const items = computed(() => {
  const _items: ListElement[] = []
  extendedParts.value.forEach(i => {
    if (i.type === 'jps-curation-list-search-result' && i.query) {
      if (!searchItem.value[i.cpid]) {
        if (!i.csId) i.csId = systemCrossSearchId()

        const searchButtonItem: Ref<ListElement> = ref({
          key: luId(),
          title: null,
          image: null,
          searchStatus: 'searching',
          description: null,
          readNext: () => {
            searchButtonItem.value.searchStatus = 'searching'
            si.value.status = 'waiting'
          },
        })

        const si: Ref<SearchItem> = ref({
          status: 'waiting',
          def: i,
          nextQuery: clone(i.query),
          items: [searchButtonItem.value],
        })
        if (!si.value.nextQuery) si.value.nextQuery.size = i.SEARCH_SIZE
        si.value.nextQuery.from = 0
        searchItem.value[i.cpid] = si.value
      }
      _items.push(...searchItem.value[i.cpid].items)
    } else {
      _items.push(toListElement(i))
    }
  })
  return _items.filter(i => i.searchStatus !== 'end')
})

const { getSearchId } = useItem()
const { searchCuration } = useCuration()
const { searchDatabase } = useDatabase()
const { searchOrganization } = useOrganization()

watch(
  () => searchItem.value,
  () => {
    Object.keys(searchItem.value)
      .map(key => searchItem.value[key])
      .forEach(async si => {
        if (si.status === 'waiting') {
          si.status = 'searching'
          let result = null
          if (si.def.dataType === 'item') {
            result = await getSearchId(si.def.csId)(si.nextQuery)
          } else if (si.def.dataType === 'curation') {
            result = await searchCuration(si.nextQuery)
          } else if (si.def.dataType === 'database') {
            result = await searchDatabase(si.nextQuery)
          } else if (si.def.dataType === 'organization') {
            result = await searchOrganization(si.nextQuery)
          }
          if (result) {
            result.list
              .filter(i => props.def.parts.every(p => p.id !== i.id))
              .forEach(i => {
                si.items.splice(
                  si.items.length - 1,
                  0,
                  dataToListElement(si.def.dataType, i),
                )
              })
          }
          si.nextQuery.from += SEARCH_SIZE
          if (result) {
            if (si.nextQuery.from >= result.hit) {
              si.status = si.items[si.items.length - 1].searchStatus = 'end'
            } else {
              si.status = si.items[si.items.length - 1].searchStatus = 'searched'
            }
          }
        }
      })
  },
  { deep: true },
)

const curationModal = ref<InstanceType<typeof CurationModal>>()
const showModal = (item: ListElement) => {
  if (props.def.openActionType == null || props.def.openActionType == 'modal') {
    const info: CurModalInfo = toModalInfo(item)
    curationModal.value.openAwait({
      info: info
    },
    {
      animation: 'slideY',
      history: info.data ? info.data.id : '0'
    })
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-curation-list {
  .jps-curation-list-title {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
}

.jps-curation-list-vt-description,
.jps-curation-list-vt-image,
.jps-curation-list-vt-map,
.jps-curation-list-vt-bullet {
  @include base-content-width();
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.jps-curation-list-vt-imagewide,
.jps-curation-list-vt-table,
.jps-curation-list-vt-tile {
  @include max-content-width();
  padding: 1.5rem 3rem;
  @include is-mobile {
    padding: 1.5rem 1rem;
  }
}

.jps-curation-list-vt-card {
  @include max-content-width();
  padding: 1.5rem 1rem;
}

.jps-curation-list-vt-reference {
  width: 100%;
  background: #f8f9f9;
  padding-top: calc(0.25rem * 2); padding-bottom: calc(0.25rem * 2);
  h2.jps-curation-list-title {
    @include base-content-width();
    font-size: 1rem !important;
    padding-bottom: 0.25rem;
  }
  .jps-curlist-reference-view {
    @include base-content-width();
  }
  @include is-tablet {
    padding-top: calc(0.25rem * 6);
    padding-bottom: calc(0.25rem * 6);
  }
}

.jps-curation-list-vt-tile {
  .grid {
    .grid-item {
      margin-bottom: 3rem;
      .card-image {
        border-radius: 0.375rem;
        overflow: hidden;
      }
      .jps-card-flexible-meta {
        display: block;
        padding: 0.25rem 0.5rem 0;
      }
      .jps-grid-description {
        padding-bottom: 0;
      }
    }
  }
}

.generated-5 {
    text-align: center;
}
</style>
